import { Component, Emit, Prop, Vue } from 'vue-property-decorator'
import ConfigurationRelationships
    from '@/modules/configurationModule/shared/components/configuration-relationships/configuration-relationships.vue'
import {
    ConfigurationAutomationProtocolsModalProtocolsFormState,
    initialConfigurationAutomationProtocolsModalProtocolsFormState
} from '@/modules/configurationModule/detail/automation/protocols/components/modal-protocols-form/configuration-automation-protocols-modal-protocols-form.model'
import {
    ConfigurationAutomaticOntologyProtocolCreateRequest,
    ConfigurationAutomaticOntologyProtocolResponse
} from '@/modules/configurationModule/detail/automation/protocols/services/configuration-automatic-ontology-protocol/configuration-automatic-ontology-protocol.dto'
import { configurationAutomaticOntologyProtocolService } from '@/modules/configurationModule/detail/automation/protocols/services/configuration-automatic-ontology-protocol/configuration-automatic-ontology-protocol.service'
import { ConfigurationProtocolResponse } from '@/modules/configurationModule/detail/local-assays/protocols/services/configuration-protocol/configuration-protocol.dto'
import { configurationProtocolService } from '@/modules/configurationModule/detail/local-assays/protocols/services/configuration-protocol/configuration-protocol.service'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import {
    ConfigurationOntologyForAutomaticProtocolCreateRequest,
    ConfigurationOntologyForAutomaticProtocolResponse
} from '@/modules/configurationModule/detail/automation/protocols/services/configuration-ontology-for-automatic-protocol/configuration-ontology-for-automatic-protocol.dto'
import { configurationOntologyForAutomaticProtocolService } from '@/modules/configurationModule/detail/automation/protocols/services/configuration-ontology-for-automatic-protocol/configuration-ontology-for-automatic-protocol.service'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        ConfigurationRelationships
    }
})
export default class ConfigurationAutomationProtocolsModalProtocolsForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly ontologyForAutomaticProtocol!: ConfigurationOntologyForAutomaticProtocolResponse

    @Emit()
    create(): void {}

    @Emit()
    delete(): void {}

    state: ConfigurationAutomationProtocolsModalProtocolsFormState
    constructor() {
        super()
        this.state = initialConfigurationAutomationProtocolsModalProtocolsFormState()
        this.state.ontologyForAutomaticProtocol = this.ontologyForAutomaticProtocol
    }

    get updateMode(): boolean {
        return !!this.state.ontologyForAutomaticProtocol?.id
    }

    private async _loadProtocols(): Promise<void> {
        this.state.protocols = await this._getProtocols()
    }

    private async _loadAutomaticOntologyProtocols(): Promise<void> {
        this.state.automaticOntologyProtocols = await this._getAutomaticOntologyProtocols()
    }

    async mounted() {
        await this._loadProtocols()
        await this._loadAutomaticOntologyProtocols()
    }

    /// SERVICES CALLS ///

    private async _getProtocols(): Promise<ConfigurationProtocolResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(configurationProtocolService.paginator(undefined, params, this.state.laboratoryId))
        if (response) {
            return response.content
        }
        return []
    }

    private async _getAutomaticOntologyProtocols(): Promise<ConfigurationAutomaticOntologyProtocolResponse[]> {
        const filterModel: CommonFilterModel = {
            'ontologyForAutomaticProtocol.id': GridUtils.getNumberFilterModel('equals', this.state.ontologyForAutomaticProtocol!.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(configurationAutomaticOntologyProtocolService.paginator(filterModel, params, this.state.laboratoryId))
        if (response) {
            return response.content
        }
        return []
    }

    private async _createAutomaticOntologyProtocol(createRequest: ConfigurationAutomaticOntologyProtocolCreateRequest): Promise<ConfigurationAutomaticOntologyProtocolResponse | null> {
        const response = await this.$_toCallUnique(configurationAutomaticOntologyProtocolService.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _deleteAutomaticOntologyProtocol(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(configurationAutomaticOntologyProtocolService.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
                    return response
                }
            }
        }
        return null
    }

    private _createOntologyForAutomaticProtocol(createRequest: ConfigurationOntologyForAutomaticProtocolCreateRequest): Promise<ConfigurationOntologyForAutomaticProtocolResponse | null> {
        return this.$_toCallUnique(configurationOntologyForAutomaticProtocolService.create(this.state.laboratoryId, createRequest))
    }

    /// FORM OPERATIONS ///

    async createOperation(protocol: ConfigurationProtocolResponse): Promise<void> {
        if (protocol) {
            if (!this.updateMode) {
                this.state.ontologyForAutomaticProtocol = await this._createOntologyForAutomaticProtocol({
                    sampleTypeId: this.ontologyForAutomaticProtocol.sampleType.id,
                    topographicCodeId: this.ontologyForAutomaticProtocol.topographicCode?.id,
                    procedureId: this.ontologyForAutomaticProtocol.procedure?.id,
                    mostLikelyMorphologicCodeId: this.ontologyForAutomaticProtocol.mostLikelyMorphologicCode?.id
                })
            }

            if (this.state.ontologyForAutomaticProtocol) {
                const createRequest: ConfigurationAutomaticOntologyProtocolCreateRequest = {
                    ontologyForAutomaticProtocolId: this.state.ontologyForAutomaticProtocol.id,
                    protocolId: protocol.id
                }
                if (await this._createAutomaticOntologyProtocol(createRequest)) {
                    await this._loadAutomaticOntologyProtocols()
                    this.create() // emits an event
                }
            }
        }
    }

    async deleteOperation(automaticOntologyProtocol: ConfigurationAutomaticOntologyProtocolResponse): Promise<void> {
        if (automaticOntologyProtocol) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: [automaticOntologyProtocol.id]
            }
            if (await this._deleteAutomaticOntologyProtocol(requestDelete)) {
                await this._loadAutomaticOntologyProtocols()
                if (!this.state.automaticOntologyProtocols.length) {
                    this.delete() // emits an event
                }
            }
        }
    }
}
